import type {FilterConfiguration, FilterConfigurationLineItem} from "~/components/ottopilotDataViewer/filterConfiguration";
import {filterLineItemsEqual} from "~/components/ottopilotDataViewer/filterLineItemEquality";
import type {SearchConfiguration} from "~/components/ottopilotDataViewer/searchConfiguration";
import {
    AMARON_MODULE_STATUS_FILTER_SCAFFOLD,
    type ModuleStatusFilterScaffold,
    type ModuleStatusNavConfig,
} from "~/domainModels/moduleStatusNav";
import {getInQueuePreservedAssignmentFilterLineItems} from "~/utilities/inQueueAssignmentFilters";

export type DataViewerFilterClearContext = {
    filterConfiguration: FilterConfiguration;
    searchConfiguration: SearchConfiguration;
    /** Applied filters excluding module-preserved (mandatory) line items. */
    clearableFilterLineItems: FilterConfiguration["lineItems"];
};

export type DataViewerFilterClearConfig = {
    /**
     * Column ids whose filter line items are kept when the user clicks Clear
     * (e.g. mandatory API filters on Emotorad Lifecycle Overview).
     */
    preservedFilterLineItemColumnIds?: ReadonlyArray<string>;
    /**
     * Exact filter line items kept on Clear (e.g. baseline `assignedTo != null` on Leads).
     * Unlike preservedFilterLineItemColumnIds, user filters on the same column are still cleared.
     */
    preservedFilterLineItems?: ReadonlyArray<FilterConfigurationLineItem>;
    /** Override when Clear should be shown. Defaults to clearable filters or non-empty search. */
    hasClearableFilters?: (context: DataViewerFilterClearContext) => boolean;
    /** Override clear behavior. Call `applyDefaultClear` to run the built-in clear. */
    onClearAllFilters?: (
        context: DataViewerFilterClearContext,
        applyDefaultClear: () => void,
    ) => void;
    /** When false, Clear does not reset search. Default true. */
    clearSearch?: boolean;
};

/**
 * Keeps sidebar scaffold status filters (Assigned / Unassigned / Live / …) when the user
 * clears toolbar or table empty-state filters.
 */
export function mergeModuleStatusFilterPreservationIntoClearConfig(
    config: DataViewerFilterClearConfig | undefined,
    args: {
        statusNavConfig: ModuleStatusNavConfig | null;
        activeStatusId: string | null;
        /** In Queue: preserve lead-owner assignment filters instead of UI status column ids. */
        inQueueLeadOwnerColumnId?: string | null;
        moduleStatusFilterScaffold?: ModuleStatusFilterScaffold;
    },
): DataViewerFilterClearConfig | undefined {
    const moduleStatusFilterScaffold =
        args.moduleStatusFilterScaffold ?? AMARON_MODULE_STATUS_FILTER_SCAFFOLD;
    const preservedColumnIds = new Set<string>(config?.preservedFilterLineItemColumnIds ?? []);
    const inQueueLeadOwnerColumnId = args.inQueueLeadOwnerColumnId;
    const inQueueAssignmentLineItems = getInQueuePreservedAssignmentFilterLineItems({
        leadOwnerColumnId: inQueueLeadOwnerColumnId,
        activeStatusId: args.activeStatusId,
    });

    if (inQueueAssignmentLineItems.length > 0) {
        const preservedLineItems: Array<FilterConfigurationLineItem> = [
            ...(config?.preservedFilterLineItems ?? []),
        ];
        for (const lineItem of inQueueAssignmentLineItems) {
            if (
                preservedLineItems.some(
                    (preserved) => filterLineItemsEqual(preserved, lineItem) === true,
                ) === false
            ) {
                preservedLineItems.push(lineItem);
            }
        }
        return {
            ...config,
            preservedFilterLineItems: preservedLineItems,
        };
    }

    if (args.statusNavConfig === null) {
        return config;
    }
    if (
        moduleStatusFilterScaffold.isModuleStatusChildView({
            statusNavConfig: args.statusNavConfig,
            activeStatusId: args.activeStatusId,
        }) !== true
    ) {
        return config;
    }

    if (
        inQueueLeadOwnerColumnId !== null &&
        inQueueLeadOwnerColumnId !== undefined &&
        inQueueLeadOwnerColumnId !== ""
    ) {
        preservedColumnIds.add(inQueueLeadOwnerColumnId);
    } else {
        const statusColumnIds = moduleStatusFilterScaffold.getModuleStatusFilterColumnIds(
            args.statusNavConfig,
        );
        for (const columnId of statusColumnIds) {
            preservedColumnIds.add(columnId);
        }
    }

    if (preservedColumnIds.size === 0) {
        return config;
    }

    return {
        ...config,
        preservedFilterLineItemColumnIds: [...preservedColumnIds],
    };
}
